import React, { useEffect, useState, useRef } from 'react'
import { Segment, Modal, Header, Table, Label, Loader } from 'semantic-ui-react'
import { useSelector } from 'react-redux'
import env from '../../../env'
import mapboxgl from '!mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import { formatTotalPrice } from '../../../lib/utils/utils'

mapboxgl.accessToken = env.REACT_APP_MAPBOX_TOKEN

function LoadBoardBidsMap({ bids, loading }) {
  const tenant = useSelector((state) => state?.globals?.tenant)
  const mapContainer = useRef(null)
  const map = useRef(null)
  const [routes, setRoutes] = useState([])
  const [selectedRoute, setSelectedRoute] = useState(null)
  const [showBidsModal, setShowBidsModal] = useState(false)

  // Process bids into routes
  useEffect(() => {
    if (!bids || bids.length === 0) return

    // Group bids by route (pickup city, state -> delivery city, state)
    const routeMap = {}

    bids.forEach((bid) => {
      const pickup = bid?.cargo_unit_instance?.pickup_location
      const delivery = bid?.cargo_unit_instance?.delivery_location

      if (!pickup?.latitude || !pickup?.longitude || !delivery?.latitude || !delivery?.longitude) {
        return
      }

      const routeKey = `${pickup.city}-${pickup.state}_${delivery.city}-${delivery.state}`

      if (!routeMap[routeKey]) {
        routeMap[routeKey] = {
          key: routeKey,
          pickup: {
            city: pickup.city,
            state: pickup.state,
            lat: pickup.latitude,
            lng: pickup.longitude,
          },
          delivery: {
            city: delivery.city,
            state: delivery.state,
            lat: delivery.latitude,
            lng: delivery.longitude,
          },
          bids: [],
          bestRate: null,
        }
      }

      routeMap[routeKey].bids.push(bid)

      // Calculate best rate (lowest accepted or pending bid)
      const bidPrice = parseFloat(bid.bid_price)
      if (!routeMap[routeKey].bestRate || bidPrice < routeMap[routeKey].bestRate) {
        if (bid.status === 'accepted' || bid.status === 'pending') {
          routeMap[routeKey].bestRate = bidPrice
        }
      }
    })

    setRoutes(Object.values(routeMap))
  }, [bids])

  // Initialize map and draw routes
  useEffect(() => {
    if (!routes || routes.length === 0 || !mapContainer.current) return

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/westonmickey/cl2gbpoe2000m16qr147gca43',
      center: [-98.5795, 39.8283], // Center of US
      zoom: 4,
    })

    map.current.on('load', () => {
      // Add route lines
      routes.forEach((route, index) => {
        const lineId = `route-${index}`
        const sourceId = `route-source-${index}`

        // Add source
        map.current.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [
                [route.pickup.lng, route.pickup.lat],
                [route.delivery.lng, route.delivery.lat],
              ],
            },
          },
        })

        // Add line layer
        map.current.addLayer({
          id: lineId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': tenant?.deployment_primary_color || '#2185d0',
            'line-width': 3,
            'line-opacity': 0.8,
          },
        })

        // Add hover effect
        map.current.on('mouseenter', lineId, () => {
          map.current.getCanvas().style.cursor = 'pointer'
        })

        map.current.on('mouseleave', lineId, () => {
          map.current.getCanvas().style.cursor = ''
        })

        // Add click handler
        map.current.on('click', lineId, () => {
          setSelectedRoute(route)
          setShowBidsModal(true)
        })

        // Add midpoint marker with best rate
        if (route.bestRate) {
          const midLat = (route.pickup.lat + route.delivery.lat) / 2
          const midLng = (route.pickup.lng + route.delivery.lng) / 2

          const el = document.createElement('div')
          el.className = 'route-label'
          el.innerHTML = `
            <div style="
              background: ${tenant?.deployment_primary_color || '#2185d0'};
              color: white;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
              font-weight: bold;
              white-space: nowrap;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            ">
              ${formatTotalPrice(route.bestRate)}
            </div>
          `

          new mapboxgl.Marker(el)
            .setLngLat([midLng, midLat])
            .addTo(map.current)
        }

        // Add pickup marker
        const pickupEl = document.createElement('div')
        pickupEl.innerHTML = `
          <div style="
            background: #21ba45;
            color: white;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: bold;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          ">
            P
          </div>
        `

        new mapboxgl.Marker(pickupEl)
          .setLngLat([route.pickup.lng, route.pickup.lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<strong>Pickup:</strong><br/>${route.pickup.city}, ${route.pickup.state}`
            )
          )
          .addTo(map.current)

        // Add delivery marker
        const deliveryEl = document.createElement('div')
        deliveryEl.innerHTML = `
          <div style="
            background: #db2828;
            color: white;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: bold;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          ">
            D
          </div>
        `

        new mapboxgl.Marker(deliveryEl)
          .setLngLat([route.delivery.lng, route.delivery.lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<strong>Delivery:</strong><br/>${route.delivery.city}, ${route.delivery.state}`
            )
          )
          .addTo(map.current)
      })

      // Fit map to show all routes
      if (routes.length > 0) {
        const bounds = new mapboxgl.LngLatBounds()
        routes.forEach((route) => {
          bounds.extend([route.pickup.lng, route.pickup.lat])
          bounds.extend([route.delivery.lng, route.delivery.lat])
        })
        map.current.fitBounds(bounds, { padding: 50 })
      }
    })

    // Add navigation control
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right')

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove()
      }
    }
  }, [routes, tenant])

  return (
    <>
      {loading && (
        <Segment style={{ minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader active>Loading bids...</Loader>
        </Segment>
      )}

      {!loading && routes.length === 0 && (
        <Segment placeholder style={{ minHeight: '600px' }}>
          <Header icon>
            <i className="map outline icon"></i>
            No routes to display
            <Header.Subheader>
              Bids must have valid pickup and delivery locations to appear on the map
            </Header.Subheader>
          </Header>
        </Segment>
      )}

      {!loading && routes.length > 0 && (
        <div style={{ position: 'relative' }}>
          <div
            ref={mapContainer}
            className="map-container"
            style={{ height: '600px', width: '100%', borderRadius: '8px' }}
          />
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              background: 'white',
              padding: '10px',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              fontSize: '12px',
            }}
          >
            <div style={{ marginBottom: '5px' }}>
              <strong>Map Legend:</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#21ba45',
                  marginRight: '5px',
                }}
              ></div>
              <span>Pickup Location</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#db2828',
                  marginRight: '5px',
                }}
              ></div>
              <span>Delivery Location</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
              <div
                style={{
                  width: '20px',
                  height: '3px',
                  background: tenant?.deployment_primary_color || '#2185d0',
                  marginRight: '5px',
                }}
              ></div>
              <span>Route (click for bids)</span>
            </div>
          </div>
        </div>
      )}

      {/* Bids Modal */}
      <Modal
        open={showBidsModal}
        onClose={() => {
          setShowBidsModal(false)
          setSelectedRoute(null)
        }}
        size="large"
      >
        <Modal.Header>
          Bids for Route: {selectedRoute?.pickup.city}, {selectedRoute?.pickup.state} →{' '}
          {selectedRoute?.delivery.city}, {selectedRoute?.delivery.state}
        </Modal.Header>
        <Modal.Content>
          {selectedRoute && (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Load ID</Table.HeaderCell>
                  <Table.HeaderCell>Trucking Company</Table.HeaderCell>
                  <Table.HeaderCell>Bid Price</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Submitted By</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {selectedRoute.bids
                  .sort((a, b) => parseFloat(a.bid_price) - parseFloat(b.bid_price))
                  .map((bid, index) => (
                    <Table.Row
                      key={bid.id}
                      positive={bid.status === 'accepted'}
                      negative={bid.status === 'rejected'}
                    >
                      <Table.Cell>
                        {bid?.cargo_unit_instance?.shipper_load_id || 'N/A'}
                      </Table.Cell>
                      <Table.Cell>{bid?.trucking_company?.name || 'N/A'}</Table.Cell>
                      <Table.Cell>
                        <strong>{formatTotalPrice(bid.bid_price)}</strong>
                        {index === 0 && bid.status !== 'rejected' && (
                          <Label
                            size="mini"
                            color="blue"
                            style={{
                              marginLeft: '5px',
                              '--primaryColor': tenant?.deployment_primary_color,
                            }}
                          >
                            Best Rate
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Label
                          color={
                            bid.status === 'accepted'
                              ? 'green'
                              : bid.status === 'rejected'
                              ? 'red'
                              : 'grey'
                          }
                        >
                          {bid.status.charAt(0).toUpperCase() + bid.status.slice(1)}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {bid?.user?.first_name} {bid?.user?.last_name}
                      </Table.Cell>
                      <Table.Cell>
                        {new Date(bid.created_at).toLocaleDateString()}
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
      </Modal>
    </>
  )
}

export default LoadBoardBidsMap
